/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { QRCodeComponent, WalletPassCard } from "@lemmonhq/uikit";
import {
  resolvePassViews,
  walletStampInk,
  walletStampProgress,
  type AppleWalletPassView,
  type GoogleWalletPassView,
  type ResolvedWalletBarcode,
  type ResolvedWalletField,
} from "@lemmonhq/blogic/wallet-design";
import { previewBindings } from "../document";
import {
  KIND_META,
  PLATFORM_META,
  SAMPLE_COIN_BALANCE,
  type PreviewTarget,
  type WalletPassDesign,
  type WalletPlatform,
} from "../types";

/**
 * Per-platform previews. Each renders the same resolved view its production
 * renderer maps — `resolvePassViews` over the design with sample bindings —
 * so a preview cannot show a field, colour or barcode that production would
 * not. Apple and Google are approximations of native chrome; the browser
 * preview is the production Collected card component itself.
 */

interface WalletPassPreviewProps {
  design: WalletPassDesign;
  target: PreviewTarget;
  platform?: WalletPlatform;
}

const FieldBlock = defineComponent<{
  field: ResolvedWalletField;
  labelColor: string;
  valueColor: string;
  size: "sm" | "lg";
  align?: "left" | "right";
}>({
  name: "WalletPreviewField",
  props: ["field", "labelColor", "valueColor", "size", "align"],
  setup(props) {
    return () => (
      <div
        class={`flex flex-col gap-0.5 min-w-0 ${
          props.align === "right" ? "items-end text-right" : "items-start"
        }`}
      >
        <span
          class="text-[8px] font-semibold tracking-widest uppercase truncate max-w-full"
          style={{ color: props.labelColor }}
        >
          {props.field.label}
        </span>
        <span
          class={`truncate max-w-full font-semibold ${
            props.size === "lg" ? "text-[15px]" : "text-[11px]"
          }`}
          style={{ color: props.valueColor }}
        >
          {props.field.value}
        </span>
      </div>
    );
  },
});

/** Diagonal-hatch placeholder standing in for an unset image slot. */
const ImageSlot = defineComponent<{ label: string; url?: string | null }>({
  name: "WalletPreviewImageSlot",
  props: ["label", "url"],
  setup(props) {
    return () =>
      props.url ? (
        <img
          src={props.url}
          alt={props.label}
          class="w-full h-full object-cover"
        />
      ) : (
        <div
          class="w-full h-full flex items-center justify-center"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.09) 0 6px, rgba(255,255,255,0.02) 6px 12px)",
          }}
        >
          <span class="text-[8px] uppercase tracking-widest text-white/50">
            {props.label}
          </span>
        </div>
      );
  },
});

/** Longer values skip the QR render. */
const MAX_QR_CHARS = 100;

const PassBarcode = defineComponent<{ barcode: ResolvedWalletBarcode | null }>({
  name: "WalletPreviewBarcode",
  props: ["barcode"],
  setup(props) {
    return () => {
      const barcode = props.barcode;
      if (!barcode) return null;
      const isQr = barcode.symbology === "qr";
      return (
        <div class="flex flex-col items-center gap-1 pb-3 pt-1">
          <div class="bg-white rounded-md p-1.5">
            {isQr && barcode.message.length <= MAX_QR_CHARS ? (
              <div class="w-[62px] h-[62px]">
                <QRCodeComponent key={barcode.message} url={barcode.message} />
              </div>
            ) : (
              <div class="w-[62px] h-[62px] flex flex-col items-center justify-center gap-1">
                <span class="material-symbols-outlined text-black/30 text-2xl">
                  {isQr ? "qr_code_2" : "barcode"}
                </span>
                <span class="text-[7px] text-black/40 uppercase">
                  {barcode.symbology}
                </span>
              </div>
            )}
          </div>
          {barcode.alt_text && (
            <span class="text-[8px] tracking-widest text-white/70">
              {barcode.alt_text}
            </span>
          )}
        </div>
      );
    };
  },
});

/** Sample reward threshold for previews; production uses the program's tier. */
const SAMPLE_COIN_REQUIRED = 10;

/**
 * Approximates the stamps production bakes into Apple/Google artwork: the
 * reward threshold as slots, each on its own solid pass-colour disc with no
 * background strip. Layout comes from walletStampProgress, as in DBOS.
 */
const PreviewStampBand = defineComponent<{
  background: string;
  foreground: string;
  placement: "top" | "bottom";
}>({
  name: "WalletPreviewStampBand",
  props: ["background", "foreground", "placement"],
  setup(props) {
    return () => {
      const progress = walletStampProgress(
        SAMPLE_COIN_BALANCE,
        SAMPLE_COIN_REQUIRED,
      );
      const ink = walletStampInk(props.background, props.foreground);
      let index = 0;
      return (
        <div
          class={`absolute inset-x-0 flex flex-col items-center gap-1 py-1.5 ${
            props.placement === "top" ? "top-[15%]" : "bottom-0"
          }`}
        >
          {progress.rows.map((count, row) => (
            <div key={row} class="flex gap-1">
              {Array.from({ length: count }, () => {
                const slot = index++;
                const reward = slot === progress.required;
                const filled = slot < progress.filled;
                return (
                  <span
                    key={slot}
                    class="rounded-full p-[1.5px] shadow"
                    style={{ backgroundColor: props.background }}
                  >
                    <span
                      class={`material-symbols-outlined rounded-full flex items-center justify-center ${
                        filled || reward ? "" : "border-dashed"
                      }`}
                      style={{
                        width: "14px",
                        height: "14px",
                        fontSize: "9px",
                        backgroundColor: filled ? ink : "transparent",
                        color: filled ? props.background : ink,
                        border: filled ? "none" : `1.5px solid ${ink}`,
                      }}
                    >
                      {reward ? "redeem" : filled ? "toll" : ""}
                    </span>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      );
    };
  },
});

const ApplePreview = defineComponent<{
  view: AppleWalletPassView;
  target: PreviewTarget;
}>({
  name: "AppleWalletPreview",
  props: ["view", "target"],
  setup(props) {
    return () => {
      const { view } = props;
      const poster = props.target === "poster" && view.poster.enabled;
      const stamps =
        view.stamps &&
        (poster
          ? view.stamps.target !== "strip"
          : view.stamps.target !== "poster");
      const logo = view.assets.logo ?? view.assets.icon;

      if (poster) {
        return (
          <div
            class="relative w-full"
            style={{
              aspectRatio: "1 / 1.15",
              backgroundColor: view.colors.background,
            }}
          >
            <div class="absolute inset-0">
              <ImageSlot label="artwork" url={view.assets.artwork} />
            </div>
            <div
              class="absolute inset-0"
              style={{
                background: `linear-gradient(to bottom, rgba(0,0,0,${view.poster.scrim_opacity * 0.5}) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,${view.poster.scrim_opacity}) 100%)`,
              }}
            />
            <div class="absolute inset-x-0 top-0 flex items-center gap-2 p-3">
              <div class="w-7 h-7 rounded-md overflow-hidden shrink-0">
                <ImageSlot label="logo" url={logo} />
              </div>
              <span
                class="text-[11px] font-semibold truncate"
                style={{ color: view.poster.text_color }}
              >
                {view.logo_text}
              </span>
            </div>
            {stamps && (
              <PreviewStampBand
                background={view.colors.background}
                foreground={view.colors.foreground}
                placement="top"
              />
            )}
            <div class="absolute inset-x-0 bottom-0 p-3 flex gap-4 flex-wrap">
              {[...view.rows.primary, ...view.rows.secondary]
                .slice(0, 4)
                .map((field) => (
                  <FieldBlock
                    key={field.key}
                    field={field}
                    labelColor={view.poster.secondary_text_color}
                    valueColor={view.poster.text_color}
                    size="sm"
                  />
                ))}
            </div>
          </div>
        );
      }

      return (
        <div>
          <div class="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
            <div class="flex items-center gap-2 min-w-0">
              <div class="w-7 h-7 rounded-md overflow-hidden shrink-0">
                <ImageSlot label="logo" url={logo} />
              </div>
              <span
                class="text-[11px] font-semibold truncate"
                style={{ color: view.colors.foreground }}
              >
                {view.logo_text}
              </span>
            </div>
            <div class="flex gap-3 shrink-0">
              {view.rows.header.map((field) => (
                <FieldBlock
                  key={field.key}
                  field={field}
                  labelColor={view.colors.label}
                  valueColor={view.colors.foreground}
                  size="sm"
                  align="right"
                />
              ))}
            </div>
          </div>
          {view.style !== "eventTicket" && (
            <div class="relative h-[76px] w-full">
              <ImageSlot label="strip" url={view.assets.artwork} />
              {stamps && (
                <>
                  <div
                    class="absolute inset-0"
                    style={{
                      backgroundColor: "#000",
                      opacity: view.stamps!.scrim_opacity,
                    }}
                  />
                  <PreviewStampBand
                    background={view.colors.background}
                    foreground={view.colors.foreground}
                    placement="bottom"
                  />
                </>
              )}
            </div>
          )}
          <div class="px-3 pt-3 pb-2 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              {view.rows.primary.map((field) => (
                <FieldBlock
                  key={field.key}
                  field={field}
                  labelColor={view.colors.label}
                  valueColor={view.colors.foreground}
                  size="lg"
                />
              ))}
            </div>
            {[view.rows.secondary, view.rows.auxiliary].map(
              (row, index) =>
                row.length > 0 && (
                  <div key={index} class="flex justify-between gap-3">
                    {row.map((field) => (
                      <FieldBlock
                        key={field.key}
                        field={field}
                        labelColor={view.colors.label}
                        valueColor={view.colors.foreground}
                        size="sm"
                      />
                    ))}
                  </div>
                ),
            )}
          </div>
        </div>
      );
    };
  },
});

const GooglePreview = defineComponent<{
  view: GoogleWalletPassView;
  fallbackHeader: string;
}>({
  name: "GoogleWalletPreview",
  props: ["view", "fallbackHeader"],
  setup(props) {
    return () => {
      const { view } = props;
      const logo = view.assets.logo ?? view.assets.icon;
      return (
        <div>
          <div class="p-4 flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-full overflow-hidden bg-white/15 flex items-center justify-center shrink-0">
                {logo ? (
                  <img
                    src={logo}
                    alt="logo"
                    class="w-full h-full object-cover"
                  />
                ) : (
                  <span
                    class="text-sm font-bold"
                    style={{ color: view.colors.foreground }}
                  >
                    {(view.logo_text || "L").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <span
                class="text-[12px] truncate"
                style={{ color: view.colors.foreground }}
              >
                {view.logo_text}
              </span>
            </div>
            <div>
              {view.subheader && (
                <div
                  class="text-[10px] truncate"
                  style={{ color: view.colors.foreground, opacity: 0.8 }}
                >
                  {view.subheader.value}
                </div>
              )}
              <div
                class="text-[18px] leading-tight font-medium truncate"
                style={{ color: view.colors.foreground }}
              >
                {view.header?.value || props.fallbackHeader}
              </div>
            </div>
            {view.rows.map((row, index) => (
              <div key={index} class="flex justify-between gap-3">
                {row.map((field, i) => (
                  <FieldBlock
                    key={field.key}
                    field={field}
                    labelColor={view.colors.foreground}
                    valueColor={view.colors.foreground}
                    size="sm"
                    align={i > 0 && i === row.length - 1 ? "right" : "left"}
                  />
                ))}
              </div>
            ))}
          </div>
          <PassBarcode barcode={view.barcode} />
          {view.assets.artwork || view.stamps ? (
            <div class="relative w-full aspect-[3/1]">
              <ImageSlot label="hero" url={view.assets.artwork} />
              {view.stamps && (
                <PreviewStampBand
                  background={view.colors.background}
                  foreground={view.colors.foreground}
                  placement="bottom"
                />
              )}
            </div>
          ) : null}
          {view.modules.length > 0 && (
            <div class="bg-white text-black/80 px-4 py-3 flex flex-col gap-2">
              {view.modules.map((field) => (
                <div key={field.key}>
                  <div class="text-[9px] uppercase tracking-wide text-black/50">
                    {field.label}
                  </div>
                  <div class="text-[11px]">{field.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };
  },
});

export const WalletPassPreview = defineComponent<WalletPassPreviewProps>({
  name: "WalletPassPreview",
  props: ["design", "target", "platform"],
  setup(props) {
    const views = computed(() =>
      resolvePassViews({
        kind: props.design.kind,
        document: props.design.document,
        bindings: previewBindings(props.design.kind),
        assetUrls: props.design.asset_urls,
      }),
    );

    return () => {
      const platform = props.platform ?? "apple";
      const view = views.value;
      const label =
        platform === "browser"
          ? "Collected card"
          : platform === "google"
            ? `${PLATFORM_META.google.label} · approximation`
            : props.target === "poster" && view.apple.poster.enabled
              ? "Poster · iOS 27+"
              : `${view.apple.style} · iOS 26 and earlier`;

      return (
        <div class="shrink-0" style={{ width: "300px" }}>
          {platform === "browser" ? (
            <div class="h-[420px]">
              <WalletPassCard
                view={view.browser}
                icon={KIND_META[props.design.kind].icon}
                fallbackTitle={props.design.name}
                stampCount={
                  props.design.kind === "coin" ? SAMPLE_COIN_BALANCE : null
                }
                stampTarget={SAMPLE_COIN_REQUIRED}
                progress={
                  props.design.kind === "coin"
                    ? {
                        value: SAMPLE_COIN_BALANCE,
                        max: SAMPLE_COIN_REQUIRED,
                        label: `${SAMPLE_COIN_REQUIRED - SAMPLE_COIN_BALANCE} to go`,
                      }
                    : null
                }
                wide={true}
                panelClass="h-44"
              />
            </div>
          ) : (
            <div
              class={`overflow-hidden shadow-xl ring-1 ring-black/10 ${
                platform === "google" ? "rounded-3xl" : "rounded-2xl"
              }`}
              style={{ backgroundColor: view.apple.colors.background }}
            >
              {platform === "google" ? (
                <GooglePreview
                  view={view.google}
                  fallbackHeader={props.design.name}
                />
              ) : (
                <>
                  <ApplePreview view={view.apple} target={props.target} />
                  <PassBarcode barcode={view.apple.barcode} />
                </>
              )}
            </div>
          )}
          <div class="text-center mt-2 text-xs text-base-content/50">
            {label}
          </div>
        </div>
      );
    };
  },
});
